import { useConfirm } from '@/hooks';
import { trans } from '@/i18n';
import { RootState } from '@/store';
import {
  resetUnsavedChanges,
  setCurrentPage,
  setHasUnsavedChanges,
  setIsBlocking,
} from '@/store/unsavedChanges';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

export const useUnsavedChanges = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleConfirm } = useConfirm();

  const { hasUnsavedChanges, currentPage, isBlocking } = useSelector(
    (state: RootState) => state.unsavedChanges
  );

  const pendingNavigationRef = useRef<string | null>(null);
  const isNavigatingRef = useRef<boolean>(false);
  const hasAddedStateRef = useRef<boolean>(false);

  useEffect(() => {
    dispatch(setCurrentPage(location.pathname));
  }, [location.pathname, dispatch]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
    };

    const handlePopState = (event: PopStateEvent) => {
      if (hasUnsavedChanges && !isNavigatingRef.current) {
        event.preventDefault();
        window.history.pushState(null, '', location.pathname);

        toggleConfirm({
          title: trans('plan.unsavedChangesTitle'),
          message: trans('plan.unsavedChangesMessage'),
          okText: trans('plan.leave'),
          cancelText: trans('plan.stay'),
          onOk: () => {
            dispatch(resetUnsavedChanges());
            isNavigatingRef.current = true;
            window.history.back();
          },
          onClose: () => {
            window.history.pushState(null, '', location.pathname);
          },
        });
      }
    };

    if (hasUnsavedChanges && !hasAddedStateRef.current) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);

      window.history.pushState(null, '', location.pathname);
      hasAddedStateRef.current = true;
    } else if (!hasUnsavedChanges && hasAddedStateRef.current) {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      hasAddedStateRef.current = false;
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, location.pathname, dispatch, toggleConfirm]);

  useEffect(() => {
    isNavigatingRef.current = false;
  }, [location.pathname]);

  const setUnsavedChanges = useCallback(
    (hasChanges: boolean) => {
      dispatch(setHasUnsavedChanges(hasChanges));
    },
    [dispatch]
  );

  const resetChanges = useCallback(() => {
    dispatch(resetUnsavedChanges());
  }, [dispatch]);

  const navigateWithConfirmation = useCallback(
    (to: string) => {
      if (hasUnsavedChanges) {
        pendingNavigationRef.current = to;
        dispatch(setIsBlocking(true));

        toggleConfirm({
          title: trans('plan.unsavedChangesTitle'),
          message: trans('plan.unsavedChangesMessage'),
          okText: trans('plan.leave'),
          cancelText: trans('plan.stay'),
          onOk: () => {
            dispatch(resetUnsavedChanges());
            dispatch(setIsBlocking(false));
            navigate(to);
            pendingNavigationRef.current = null;
          },
          onClose: () => {
            dispatch(setIsBlocking(false));
            pendingNavigationRef.current = null;
          },
        });
      } else {
        navigate(to);
      }
    },
    [hasUnsavedChanges, dispatch, navigate, toggleConfirm]
  );

  return {
    hasUnsavedChanges,
    currentPage,
    isBlocking,
    setUnsavedChanges,
    resetChanges,
    navigateWithConfirmation,
  };
};

export const useFormUnsavedChanges = (hasChanges: boolean) => {
  const { setUnsavedChanges, resetChanges } = useUnsavedChanges();

  useEffect(() => {
    setUnsavedChanges(hasChanges);
  }, [hasChanges, setUnsavedChanges]);

  return { resetChanges };
};
